import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Card, Col, Container, Form, Row, Spinner } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../components/auth/AuthProvider';
import DireccionForm from '../../components/perfil/DireccionForm';
import { useToast } from '../../components/toast/ToastContext';
import { getComunidadAdmin, getOrg, saveComunidadAdmin, setOrgToken } from '../../controllers/org';

function ComunidadAdmin() {
  const params = useParams();
  const navigate = useNavigate();
  const { token, hasPermission } = useAuth();
  const showToast = useToast();
  const direccionRef = useRef();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [comunidades, setComunidades] = useState([]);
  const [comunidad, setComunidad] = useState(null);
  const [servicios, setServicios] = useState([]);
  const [ciclo, setCiclo] = useState(() => {
    const now = new Date();
    return now.getMonth() < 7 ? now.getFullYear() - 1 : now.getFullYear();
  });
  const [error, setError] = useState('');

  const canManageAll = hasPermission('administrar_todas_las_comunidades');
  const canManageOwn = hasPermission('administrar_comunidad');

  function getCurrentCommunityId() {
    if (!token) {
      return null;
    }
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload?.authInfo?.membership?.comunidad_id ?? null;
    } catch (e) {
      return null;
    }
  }

  useEffect(() => {
    if (!canManageAll && !canManageOwn) {
      setError('No tienes permiso para administrar comunidades.');
      setLoading(false);
      return;
    }

    setOrgToken(token);
    loadCommunities();
  }, [token, canManageAll, canManageOwn]);

  useEffect(() => {
    if (!params.comunidadId) {
      setComunidad(null);
      setServicios([]);
      setLoading(false);
      return;
    }

    loadData(params.comunidadId, ciclo);
  }, [params.comunidadId, ciclo, token, hasPermission]);

  async function loadCommunities() {
    try {
      const data = await getOrg();
      setComunidades(data.comunidades || []);
      if (params.comunidadId) {
        return;
      }
      if (canManageAll) {
        if (data.comunidades?.length) {
          navigate(`/app/admin/comunidades/${data.comunidades[0].comunidad_id}`);
        }
        return;
      }
      const ownCommunity = data.comunidades?.find(item => item.comunidad_id === Number(getCurrentCommunityId()));
      if (ownCommunity) {
        navigate(`/app/admin/comunidades/${ownCommunity.comunidad_id}`);
        return;
      }
      if (data.comunidades?.length) {
        navigate(`/app/admin/comunidades/${data.comunidades[0].comunidad_id}`);
      }
    } catch (e) {
      setError(e.message || 'No se pudo cargar la lista de comunidades.');
    }
  }

  async function loadData(comunidadId, currentCiclo) {
    try {
      setLoading(true);
      const data = await getComunidadAdmin(comunidadId, currentCiclo);
      setComunidad(data.comunidad);
      setServicios(data.servicios || []);
      setError('');
    } catch (e) {
      setError(e.message || 'No se pudo cargar la información de la comunidad.');
    } finally {
      setLoading(false);
    }
  }

  const canSave = useMemo(() => Boolean(comunidad && servicios.length), [comunidad, servicios]);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!canSave || !params.comunidadId) return;

    const direccion = direccionRef.current?.getDireccion();
    setSaving(true);
    try {
      await saveComunidadAdmin(params.comunidadId, {
        direccion,
        ciclo,
        servicios: servicios.filter(item => item.selected).map(item => item.id)
      });
      showToast({
        message: 'Se actualizó la información de la comunidad.',
        bg: 'success',
        autohide: true,
        delay: 7000,
        title: 'Comunidad actualizada'
      });
    } catch (e) {
      showToast({
        message: e.message || 'No se pudo guardar la información de la comunidad.',
        bg: 'danger',
        autohide: true,
        delay: 7000,
        title: 'Error al actualizar comunidad'
      });
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <div className="py-4"><Spinner animation="border" /></div>;
  }

  if (error) {
    return <Alert variant="danger" className="mt-4">{error}</Alert>;
  }

  return (
    <Container className="py-4">
      <h1>Administrar comunidades</h1>
      <Form.Group className="mb-4">
        <Form.Label>Selecciona una comunidad</Form.Label>
        <Form.Select value={params.comunidadId || ''} onChange={e => navigate(`/app/admin/comunidades/${e.target.value}`)} disabled={!canManageAll}>
          <option value="">-- Selecciona una comunidad --</option>
          {comunidades.map(item => (
            <option key={item.comunidad_id} value={item.comunidad_id}>{item.label}</option>
          ))}
        </Form.Select>
        {!canManageAll && <div className="form-text">Con el permiso de comunidad solo puedes editar tu propia comunidad.</div>}
      </Form.Group>

      {!params.comunidadId ? null : (
        <Form onSubmit={handleSubmit}>
          <p className="text-muted">{comunidad?.label}</p>
          <Card className="mb-4">
            <Card.Header>Dirección de la comunidad</Card.Header>
            <Card.Body>
              <DireccionForm ref={direccionRef} direccion={{
                direccion: comunidad?.address_1,
                direccion2: comunidad?.address_2,
                ciudad: comunidad?.city,
                estado: comunidad?.state,
                zipCode: comunidad?.zip_code,
              }} />
            </Card.Body>
          </Card>

          <Card className="mb-4">
            <Card.Header>Servicios disponibles por ciclo</Card.Header>
            <Card.Body>
              <Form.Group className="mb-3">
                <Form.Label>Ciclo</Form.Label>
                <Form.Control type="number" value={ciclo} onChange={e => setCiclo(parseInt(e.target.value || 0))} />
              </Form.Group>
              <Row xs={1} md={2} className="g-3">
                {servicios.map(servicio => (
                  <Col key={servicio.id}>
                    <Form.Check
                      type="checkbox"
                      id={`servicio-${servicio.id}`}
                      label={servicio.label}
                      checked={Boolean(servicio.selected)}
                      onChange={() => setServicios(current => current.map(item => item.id === servicio.id ? { ...item, selected: !item.selected } : item))}
                    />
                  </Col>
                ))}
              </Row>
            </Card.Body>
          </Card>

          <Button type="submit" variant="primary" disabled={saving || !canSave}>
            {saving ? 'Guardando...' : 'Guardar cambios'}
          </Button>
        </Form>
      )}
    </Container>
  );
}

export default ComunidadAdmin;
